import { DOCUMENT, isPlatformServer } from '@angular/common';
import {
	computed,
	effect,
	inject,
	Injectable,
	PLATFORM_ID,
	signal
} from '@angular/core';
import { LocalStorage } from 'app/core/local-storage/local-storage';
import { Media } from 'app/core/media/media';
import { TonalPalette } from 'app/core/theming/palette';

import { Colors, Scheme, Theme } from './models/theming';
import { THEME_CONFIG } from './provider';

@Injectable({ providedIn: 'root' })
export class Theming {
	colors;
	scheme;
	theme;
	isDark;
	isLight;

	private readonly document = inject(DOCUMENT);
	private readonly isServer = isPlatformServer(inject(PLATFORM_ID));
	private readonly localStorage = inject(LocalStorage);
	private readonly media = inject(Media);
	private readonly themeConfig = inject(THEME_CONFIG);

	private readonly prefersDarkMode = this.media.match(
		'(prefers-color-scheme: dark)'
	);

	private readonly rootEl = this.document.documentElement;
	private readonly themeStyleEl = this.document.createElement('style');

	constructor() {
		this.colors = signal<Colors>({
			primary: this.themeConfig.primary,
			accent:  this.themeConfig.accent,
			error:   this.themeConfig.error
		});
		this.scheme = signal<Scheme>(
			(this.localStorage.getItem('scheme') as Scheme)
				|| this.themeConfig.scheme
		);
		this.theme = computed<Theme>(() => this.generateTheme(this.colors()));
		this.isDark = computed(
			() =>
				this.scheme() === 'dark'
				|| (this.scheme() === 'system' && this.prefersDarkMode())
		);
		this.isLight = computed(() => !this.isDark());

		this.document.head.appendChild(this.themeStyleEl);
		this.themeStyleEl.classList.add('theme-colors');

		effect(() => {
			if (this.isServer) {
				return;
			}

			const scheme = this.scheme();
			const prefersDarkMode = this.prefersDarkMode();

			const isDark =
				scheme === 'dark' || (scheme === 'system' && prefersDarkMode);

			this.rootEl.classList.toggle('scheme-dark', isDark);
			this.rootEl.classList.toggle('scheme-light', !isDark);

			this.localStorage.setItem('scheme', scheme);
		});

		this.generateTheme(this.themeConfig);
	}

	/**
	 * Generates a theme using the provided theming configuration and
	 * applies it to the DOM by injecting CSS variables into a
	 * style element.
	 */
	private generateTheme(config: Colors): Theme {
		const primary = new TonalPalette({
			color: config.primary
		});
		const accent = new TonalPalette({
			color: config.accent
		});
		const error = new TonalPalette({
			color: config.error
		});

		const hues = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
		const paletteVariables = (
			name: string,
			palette: TonalPalette
		): string =>
			hues
				.map(
					hue => `--theme-color-${name}-${hue}: ${palette.hue(hue)};`
				)
				.join('\n\t\t\t');

		this.themeStyleEl.textContent = `:root {
			${paletteVariables('primary', primary)}
			${paletteVariables('accent', accent)}
			${paletteVariables('error', error)}
		}`;

		return {
			primary,
			accent,
			error
		};
	}
}
